import React from "react";
import { HardHat } from "lucide-react";
import CrudModule, { useEmployees } from "@/components/CrudModule";
import { Badge2 } from "@/components/ComplianceBadge";
import { LegalCompliance } from "@/components/LegalCompliance";
import { PpeConfirm, PpeEstorno, EsocialView } from "@/components/PpeActions";

const locked = (r) => !!(r.locked || r.signature || ["ESTORNADO", "Estorno"].includes(r.status));

const statusBadge = (r) => {
  if (r.status === "ESTORNADO") return <Badge2 color="red">[ESTORNADO]</Badge2>;
  if (r.status === "Estorno") return <Badge2 color="slate">Estorno</Badge2>;
  if (r.validation_method === 1) return <Badge2 color="green">Biometria</Badge2>;
  if (r.validation_method === 2 || r.signature) return <Badge2 color="green">Assinatura</Badge2>;
  return <Badge2 color="yellow">Pendente</Badge2>;
};

const rowExtra = (row, reload) => {
  if (row.status === "Estorno") return null;
  return (
    <>
      {!locked(row) && <PpeConfirm row={row} onDone={reload} />}
      {row.payload_esocial_pronto && <EsocialView row={row} />}
      {row.status !== "ESTORNADO" && (row.locked || row.signature) && <PpeEstorno row={row} onDone={reload} />}
    </>
  );
};

export default function PPE() {
  const employees = useEmployees();
  return (
    <CrudModule
      title="Controle de EPIs (NR-06)"
      subtitle="Entrega rastreável com biometria ou assinatura, eSocial S-2240 e estorno"
      icon={HardHat}
      endpoint="ppe-deliveries"
      exportKey="ppe-deliveries"
      testid="ppe"
      employees={employees}
      employeeFilter
      fichaPath="ppe-deliveries/ficha"
      catalogEndpoint="ppe-catalog"
      headerExtra={<LegalCompliance />}
      rowExtra={rowExtra}
      rowLocked={locked}
      noDelete
      columns={[
        { key: "delivery_date", label: "Data" },
        { key: "employee_name", label: "Colaborador" },
        { key: "ppe_name", label: "EPI" },
        { key: "ca_number", label: "CA" },
        { key: "lot_number", label: "Lote" },
        { key: "risk_category", label: "Cat." },
        { key: "ca_expiry", label: "Validade CA", badge: "validity" },
        { key: "quantity", label: "Qtd" },
        { key: "reason", label: "Motivo" },
        { key: "status", label: "Validação", render: statusBadge },
        { key: "doc", label: "Ficha", file: true, idKey: "document_id" },
      ]}
      fields={[
        { name: "employee", label: "Colaborador", type: "employee", idKey: "employee_id", nameKey: "employee_name", required: true, full: true },
        { name: "ppe_name", label: "EPI (busque no catálogo)", type: "catalog", required: true, full: true,
          fill: { ca_number: "ca_number", ca_expiry: "ca_expiry", product_expiry: "product_expiry", risk_category: "risk_category", esocial_description: "esocial_description", complement_ppe: "complement_ppe" } },
        { name: "ca_number", label: "Número do CA", type: "text", required: true },
        { name: "ca_expiry", label: "Validade do CA (MTE)", type: "date" },
        { name: "lot_number", label: "Lote do fabricante", type: "text", required: true },
        { name: "product_expiry", label: "Validade do produto (lote)", type: "date" },
        { name: "invoice_date", label: "Data da Nota Fiscal de compra", type: "date" },
        { name: "risk_category", label: "Categoria de risco (Port. 1.369/2024)", type: "select", options: ["I", "II", "III"], default: "I" },
        { name: "complement_ppe", label: "Item complementar obrigatório (Cat. III)", type: "text" },
        { name: "complement_included", label: "Item complementar entregue junto?", type: "select", options: ["Não se aplica", "Sim", "Não"], default: "Não se aplica" },
        { name: "esocial_description", label: "Descrição eSocial", type: "text", full: true },
        { name: "quantity", label: "Quantidade", type: "number", default: 1 },
        { name: "delivery_date", label: "Data de entrega", type: "date", required: true },
        { name: "reason", label: "Motivo", type: "select", options: ["Admissão", "Periódico", "Danificado", "Extravio"], default: "Admissão" },
        { name: "document", label: "Ficha assinada (upload opcional)", type: "file", idKey: "document_id", nameKey: "document_name", full: true },
      ]}
    />
  );
}
